import React, { useState } from 'react';
import KPICard from '../ui/KPICard';
import Card from '../ui/Card';
import Section from '../ui/Section';
import Table from '../ui/Table';
import Badge from '../ui/Badge';
import AlertsSection from '../ui/AlertsSection';
import { formatCurrency, formatNumber } from '../dashboardUtils';
import { useTopProductsQuery } from '../../../../api/sales.api';
import { useInventoryValueQuery } from '../../../../api/dashboard.api';
import { useListAllQuery } from '../../../../api/product.api';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
} from 'recharts';

/**
 * Product Analytics View Component
 */
const ProductAnalyticsView = ({ dateRange }) => {
    const { from, to } = dateRange;
    const [sortBy, setSortBy] = useState('revenue');

    const { data: topProductsData, isLoading: productsLoading } = useTopProductsQuery({
        sortBy,
        from,
        to,
    });
    const { data: inventoryData, isLoading: inventoryLoading } = useInventoryValueQuery();
    const { data: allProductsData, isLoading: allLoading } = useListAllQuery({});

    const productsList = topProductsData?.result?.products || [];
    const summary = topProductsData?.result?.summary || {};
    const inventoryInfo = inventoryData?.result || {};
    const totalCatalogProducts = allProductsData?.meta?.total || productsList.length;

    // Format top 8 products for Recharts Bar Chart
    const chartData = productsList.slice(0, 8).map((p) => ({
        name: p.productName?.length > 14 ? `${p.productName.slice(0, 14)}...` : p.productName || 'Product',
        Revenue: p.revenue || 0,
        Profit: p.profit || 0,
        Units: p.unitsSold || 0,
    }));

    // Table Columns Config
    const tableColumns = [
        {
            key: 'rank',
            label: 'Rank',
            align: 'center',
            render: (_, __, index) => {
                const rankBadge =
                    index === 0
                        ? 'bg-amber-400 text-amber-950 font-bold'
                        : index === 1
                        ? 'bg-slate-300 text-slate-900 font-bold'
                        : index === 2
                        ? 'bg-orange-400 text-white font-bold'
                        : 'bg-stone-100 text-stone-600 font-medium';
                return (
                    <span
                        className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs ${rankBadge}`}
                    >
                        {index + 1}
                    </span>
                );
            },
        },
        {
            key: 'productName',
            label: 'Product Name',
            render: (val, row) => (
                <div>
                    <div className="font-semibold text-stone-900">{val || 'Product Item'}</div>
                    <div className="text-[11px] text-stone-400 font-normal">
                        ID: {row.productId ? row.productId.slice(-8) : '-'}
                    </div>
                </div>
            ),
        },
        {
            key: 'unitsSold',
            label: 'Units Sold',
            align: 'right',
            render: (val) => (
                <span className="font-semibold text-stone-800">{formatNumber(val)}</span>
            ),
        },
        {
            key: 'revenue',
            label: 'Revenue',
            align: 'right',
            render: (val) => (
                <span className="font-semibold text-stone-900">{formatCurrency(val)}</span>
            ),
        },
        {
            key: 'profit',
            label: 'Profit',
            align: 'right',
            render: (val) => (
                <span className="font-semibold text-emerald-600">{formatCurrency(val)}</span>
            ),
        },
        {
            key: 'profitMargin',
            label: 'Margin %',
            align: 'right',
            render: (margin) => {
                const variant = margin >= 50 ? 'success' : margin >= 25 ? 'warning' : 'danger';
                return <Badge variant={variant}>{margin || 0}%</Badge>;
            },
        },
    ];

    return (
        <div className="space-y-8 w-full min-w-0">
            {/* KPI Cards */}
            <Section title="Product Performance Metrics" subtitle="Catalog analytics and stock valuations">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 w-full min-w-0">
                    <KPICard
                        label="Catalog Products"
                        value={formatNumber(totalCatalogProducts)}
                        loading={allLoading}
                        accentClass="bg-stone-900 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Total Units Sold"
                        value={formatNumber(summary.totalUnitsSold || 0)}
                        loading={productsLoading}
                        accentClass="bg-indigo-600 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 11h14l1 12H4L5 11z" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Top Product Revenue"
                        value={formatCurrency(productsList[0]?.revenue || 0)}
                        sub={productsList[0]?.productName ? `${productsList[0].productName.slice(0, 15)}...` : 'Top Item'}
                        loading={productsLoading}
                        accentClass="bg-emerald-600 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Inventory Valuation"
                        value={formatCurrency(inventoryInfo.totalValue || inventoryInfo.inventoryValue || 0)}
                        loading={inventoryLoading}
                        accentClass="bg-purple-600 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7e6 0 011 1h10a1 1 0 011 1v5a1 1 0 01-1 1H2a1 1 0 01-1-1V8z" />
                            </svg>
                        }
                    />
                </div>
            </Section>

            {/* Recharts Bar Chart */}
            <Section
                title="Top Product Performance Chart"
                subtitle="Comparative revenue and profit by product"
                action={
                    <div className="flex items-center gap-1 bg-stone-100 p-1 rounded-xl border border-stone-200">
                        <button
                            onClick={() => setSortBy('revenue')}
                            className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                                sortBy === 'revenue'
                                    ? 'bg-stone-900 text-white shadow-xs'
                                    : 'text-stone-600 hover:text-stone-900'
                            }`}
                        >
                            Sort by Revenue
                        </button>
                        <button
                            onClick={() => setSortBy('units')}
                            className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                                sortBy === 'units'
                                    ? 'bg-stone-900 text-white shadow-xs'
                                    : 'text-stone-600 hover:text-stone-900'
                            }`}
                        >
                            Sort by Units
                        </button>
                    </div>
                }
            >
                <Card noPadding>
                    <div className="p-6 w-full min-w-0">
                        {productsLoading ? (
                            <div className="h-72 flex items-center justify-center text-xs text-stone-400">
                                Loading top products chart...
                            </div>
                        ) : chartData.length === 0 ? (
                            <div className="h-72 flex items-center justify-center text-xs text-stone-400">
                                No product sales recorded in this timeframe
                            </div>
                        ) : (
                            <div className="h-80 w-full min-w-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={chartData}
                                        margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
                                        <YAxis stroke="#94a3b8" fontSize={11} />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#0f172a',
                                                borderRadius: '0.75rem',
                                                color: '#f8fafc',
                                                fontSize: '12px',
                                            }}
                                            formatter={(val) => [formatCurrency(val), '']}
                                        />
                                        <Legend verticalAlign="top" align="right" wrapperStyle={{ paddingBottom: '15px', fontSize: '12px' }} />
                                        <Bar dataKey="Revenue" fill="#0f172a" radius={[6, 6, 0, 0]} />
                                        <Bar dataKey="Profit" fill="#10b981" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </Card>
            </Section>

            {/* Top Products Table */}
            <Section title="Top Performing Products Leaderboard" subtitle="Detailed breakdown of bestsellers">
                <Table
                    columns={tableColumns}
                    data={productsList}
                    keyField="productId"
                    highlightTopRank
                    emptyMessage="No product data found"
                />
            </Section>

            {/* Inventory Alerts Section */}
            <Section title="Inventory Controls" subtitle="Products requiring restock or liquidations">
                <AlertsSection threshold={5} days={30} />
            </Section>
        </div>
    );
};

export default ProductAnalyticsView;
